import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { debounce } from '../../../helpers/debounce';
import AgGridComponent from '../../../sharedcomponents/ag-grid/AgGrid';
import {
  Active,
  AutoSelectPkgDist,
  Cancelled,
  Endpoints,
  GenericError,
  PendingActive,
  PendingApproval,
  PendingInactive,
  ProductDeliveryPlatforms,
  ProductTypes,
  ReduxAction,
  Rejected,
  Draft,
  RequiredPlatformCategory,
  UserId,
  Blocked,
  Verbiage,
} from './../../../constants';
import store from './../../../redux/store';
import smartHttp from '../../../helpers/smartHttp';
import { Notify } from './../../../sharedcomponents/Alert/Notify';
import ProductsDropdown from './../../Entitlements/ProductsDropdown';
// FEATURE DISABLED (Release july-2026) - Delivery Platform Dropdown
// import ProductDistributorsDropdown from './../../Entitlements/ProductDistributorsDropdown';
import StatusDefinitionHeader, {
  getStatusDefinition,
} from './../../Entitlements/StatusDefinitionHeader';
import InfoIcon from './../../../assets/icons/common/info.png';
import StickyTooltip from './../../../sharedcomponents/StickyTooltip/StickyTooltip';

const UserProductEntitlements = memo(
  ({
    contractNumber,
    editMode,
    distributorDataSet,
    userEntitlementData,
    packageMappingData,
    editData,
    onAddEditModalClose,
    getUsers,
    setLoading,
    setActiveTab,
    setIsUserIdEnabled,
    isViewerUser,
    userContractNumber,
    userContractInfo,
    updateEmailPreferencesChanged,
    distributorToIndicate,
    isBPMReferral,
  }) => {
    //state variable declarations starts
    const [productRowData, setProductRowData] = useState();
    const [productDataset, setProductDataset] = useState();
    // FEATURE DISABLED (Release july-2026) - Delivery Platform Dropdown
    // const [deliveryPltfDataset, setDeliveryPltfDataset] = useState();
    const [selectedProducts, setSelectedProducts] = useState([]);
    // const [selectedDeliveryPltf, setSelectedDeliveryPltf] = useState([]);
    // const [selectedDeliveryPltfId, setSelectedDeliveryPltfId] = useState([]);
    const [selectedDeliveryPltf] = useState([]); // FEATURE DISABLED (Release july-2026) - read-only stub
    const [selectedDeliveryPltfId] = useState([]); // FEATURE DISABLED (Release july-2026) - read-only stub
    const [selectedParentCascade, setSelectedParentCascade] = useState();
    const [filteredProducts, setFilteredProducts] = useState();
    // const [filteredDeliveryPlatforms, setFilteredDeliveryPlatforms] = useState();
    const [availableMappings, setAvailableMappings] = useState();
    const [uniqAvailableMappings, setUniqAvailableMappings] = useState();
    const [initialEntitlements, setInitialEntitlements] = useState([]);
    const [initialEntitlementIds, setInitialEntitlementIds] = useState([]);
    const [deselectRowsData, setDeselectRowsData] = useState([]);
    const [isEntitlementsChanged, setIsEntitlementsChanged] = useState(false);
    const [isFirstDataSelection, setIsFirstDataSelection] = useState(true);
    const [isRowDataLoaded, setIsRowDataLoaded] = useState(false);
    const [resetNonce, setResetNonce] = useState(0);
    const productEntitlementgridApi = useRef(null);
    const dispatch = useDispatch();
    const InActiveStatus = [Cancelled, PendingInactive, Rejected];
    const [tooltip, setTooltip] = useState({});
    const tooltipRef = useRef(null);
    const gridContainerRef = useRef(null);
    //state variable declarations ends

    const onProductEntitlementGridReadyHandler = (params) => {
      productEntitlementgridApi.current = params;
      // If we already have product row data resolved by the time the grid
      // becomes ready (e.g. user visited the Entitlements tab first, then
      // switched here), don't flash the loading overlay — the data-driven
      // useEffect below has already filtered the rows. Otherwise show the
      // loading overlay until data arrives.
      if (productRowData && productRowData.length > 0) {
        params.api.hideOverlay();
      } else if (
        editData &&
        userEntitlementData &&
        productRowData &&
        productRowData.length === 0
      ) {
        params.api.showNoRowsOverlay();
      } else {
        params.api.showLoadingOverlay();
      }
    };

    const isFirstColumn = (params) => {
      let displayedColumns = params?.api?.getAllDisplayedColumns();
      let thisIsFirstColumn = displayedColumns[0] === params?.column;
      if (thisIsFirstColumn) {
        return thisIsFirstColumn;
      }
    };
    const defaultConfig = {
      rowModelType: 'clientSide',
      isExportCSV: false,
      isExportExcel: false,
      suppressDragLeaveHidesColumns: true,
      pivotPanelShow: '',
      pagination: false,
      isAutoSizeColumns: true,
      enableCharts: false,
      rowGroupPanelShow: false,
      sideBar: false,
      overlayNoRowsTemplate: 'This user has no entitlements',
    };

    const defaultColdef = {
      sortable: true,
      resizable: true,
      headerCheckboxSelection:
        editData?.groupId || isViewerUser ? false : isFirstColumn,
      // custom checkbox renderer to show selected+disabled for blocked
      checkboxSelection: false,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
    };

    const validateEntitlementChange = () => {
      let currentlySelectedEntitlements =
        productEntitlementgridApi?.current?.api?.getSelectedRows();
      let initialEntitlementsToValidateChanges = initialEntitlements?.filter(
        (x) =>
          x.status != Cancelled &&
          x.status != PendingInactive &&
          x.status != Rejected,
      );
      if (
        currentlySelectedEntitlements &&
        initialEntitlementsToValidateChanges
      ) {
        // Compare based on productComponentId instead of full object equality
        const getIds = (arr) =>
          (arr || []).map((x) => x.productComponentId).sort();
        const selectedIds = getIds(currentlySelectedEntitlements);
        const initialIds = getIds(initialEntitlementsToValidateChanges);

        const arraysEqual = (a, b) =>
          a.length === b.length && a.every((v, i) => v === b[i]);

        if (arraysEqual(selectedIds, initialIds)) {
          setIsEntitlementsChanged(false);
          updateEmailPreferencesChanged(false);
        } else {
          setIsEntitlementsChanged(true);
          updateEmailPreferencesChanged(true);
        }
      }
      return currentlySelectedEntitlements ?? [];
    };

    const setSelectionsInGrid = () => {
      // Run further if there are any entitlements mapping available
      if (!productEntitlementgridApi.current) return;
      productEntitlementgridApi.current.api.selectAll();
      const nodesToDeselect = [];
      if (isFirstDataSelection) {
        setIsFirstDataSelection(false);
        productEntitlementgridApi.current.api.forEachNode((node) => {
          // Deselect Cancelled and Pending Inactive entitlements
          if (
            [Cancelled, PendingInactive, Rejected].includes(node?.data?.status)
          )
            nodesToDeselect.push(node);
        });
      }

      if (isRowDataLoaded && deselectRowsData?.length > 0) {
        productEntitlementgridApi?.current?.api?.forEachNode((node) => {
          if (
            deselectRowsData.some(
              (nd) => nd.productComponentId === node.data.productComponentId,
            )
          )
            nodesToDeselect.push(node);
        });
      }
      productEntitlementgridApi.current.api.setNodesSelected({
        nodes: nodesToDeselect,
        newValue: false,
      });
      setDeselectRowsData([]);
      setIsRowDataLoaded(true);
      validateEntitlementChange();
    };

    const getEntitlementChanges = () => {
      let contactId = editData.contactId !== '' ? editData.contactId : -1;
      let entitlementChanges = [];
      let newEntitlementChanges = [];

      productEntitlementgridApi?.current?.api?.forEachNode((node) => {
        // remove entitlements that have a status and aren't selected
        if (
          node.data.status &&
          [Active, PendingActive, PendingApproval, Draft, Blocked].includes(
            node.data.status,
          ) &&
          !node.isSelected()
        ) {
          entitlementChanges.push({
            contactid: contactId,
            productcomponentid: node.data.productComponentId,
            status: PendingInactive,
            contractNumber: node.data.contractNumber,
            userEntitlementId: node.data.userEntitlementId,
            packageName: node.data.packageName,
            distributorId: node.data.distributorId,
            distributor: node.data.distributor,
            distributorPlatform: node.data.distributorPlatform,
          });
        }
        // add entitlements that don't have a status and are selected
        if (
          (!node.data.status ||
            [PendingInactive, Cancelled, Rejected].some(
              (status) => node.data?.status === status,
            )) &&
          node.isSelected()
        ) {
          const newEntitlement = {
            contactid: contactId,
            productcomponentid: node.data.productComponentId,
            // FEATURE DISABLED (Release july-2026) - status: isBPMReferral ? Draft : PendingActive,
            status: PendingActive,
            contractNumber: node.data.contractNumber,
            packageName: node.data.packageName,
            distributorId: node.data.distributorId,
            distributor: node.data.distributor,
            distributorPlatform: node.data.distributorPlatform,
          };
          entitlementChanges.push(newEntitlement);
          // Add into this array if product's parent package is Market Basics
          if (
            node.data.packageName.includes(
              AutoSelectPkgDist.Market_Basics.pkgName,
            )
          ) {
            newEntitlementChanges.push(newEntitlement);
          }
        }
      });

      // Add Market Basics entitlements data if Product has same Market Basics Packages
      if (newEntitlementChanges.length > 0) {
        let updatedMappings = packageMappingData;
        if (userContractNumber?.length > 0) {
          updatedMappings = packageMappingData.filter((am) =>
            userContractNumber?.includes(am.contractNumber),
          );
        }
        const filteredUserEntitlementsData = updatedMappings.filter(
          (uedata) =>
            !ProductDeliveryPlatforms.includes(uedata.distributor) &&
            uedata.packageName.includes(
              AutoSelectPkgDist.Market_Basics.pkgName,
            ) &&
            uedata.distributorId == AutoSelectPkgDist.Market_Basics.distId &&
            newEntitlementChanges.some(
              (ec) => ec.packageName == uedata.packageName,
            ),
        );

        filteredUserEntitlementsData.forEach((uedata) => {
          const existingEntitlement = productRowData.find(
            (ue) => ue.productComponentId === uedata.productComponentId,
          );

          if (
            (existingEntitlement &&
              [PendingInactive, Cancelled, Rejected].includes(
                existingEntitlement.status,
              )) ||
            !existingEntitlement
          ) {
            entitlementChanges.push({
              contactId: contactId,
              productComponentId: uedata.productComponentId,
              // FEATURE DISABLED (Release july-2026) - status: isBPMReferral ? Draft : PendingActive,
              status: PendingActive,
              contractNumber: uedata.contractNumber,
            });
          }
        });
      }

      return entitlementChanges;
    };

    //useEffects start
    useEffect(() => {
      // Controller to abort previous API calls if any ongoing flight
      const controller = new AbortController();
      const signal = controller.signal;
      if (
        editMode &&
        userContractNumber?.length > 0 &&
        distributorToIndicate?.length >= 0 // check if Set has elements
      ) {
        getContractProducts(signal);
      }
      return () => {
        controller?.abort();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, userContractNumber, distributorToIndicate]);

    // Set up initial filtered datasets
    useEffect(() => {
      if (productDataset) setFilteredProducts(productDataset);
      // FEATURE DISABLED (Release july-2026)
      // if (deliveryPltfDataset) setFilteredDeliveryPlatforms(deliveryPltfDataset);
    }, [productDataset]);

    // FEATURE DISABLED (Release july-2026) - Filter delivery platforms based on product selection
    // useEffect(() => { ... }, [selectedProducts]);

    // FEATURE DISABLED (Release july-2026) - Filter products based on delivery platform selection
    // useEffect(() => { ... }, [selectedDeliveryPltf, selectedDeliveryPltfId]);

    useEffect(() => {
      let isMounted = true;
      if (isMounted) {
        // Disable loading once api is completed
        if (
          editData?.length === 0 &&
          userEntitlementData?.length === 0 &&
          productEntitlementgridApi?.current
        ) {
          productEntitlementgridApi.current?.api?.showNoRowsOverlay();
        }

        if (editData && userEntitlementData) {
          let ids = [];
          let currentUserEntitlements = userEntitlementData.filter((ue) => {
            let baseMatch =
              ue.contactId === editData.contactId &&
              ProductDeliveryPlatforms.includes(ue.distributor)
            || ProductTypes.includes(ue.productType)
       

            const match =
              baseMatch && userContractNumber?.includes(ue.contractNumber);
            if (match) ids.push(ue.productComponentId);
            return match;
          });
          setInitialEntitlements(currentUserEntitlements);
          setInitialEntitlementIds(ids);
          setProductRowData(currentUserEntitlements);
          dispatch({
            type: ReduxAction.updateProductEntitlement,
            payload: {},
          });
          if (currentUserEntitlements?.length === 0) {
            setIsRowDataLoaded(true);
            productEntitlementgridApi.current?.api.showNoRowsOverlay();
          } else {
            productEntitlementgridApi.current?.api.hideOverlay();
          }
        }
      }
      return () => {
        isMounted = false;
      };
    }, [userEntitlementData, editData]);

    useEffect(() => {
      let isMounted = true;
      if (isMounted) {
        // clear tooltip if grid data changes
        setTooltip({});
        // Cartesian Start
        if (
          selectedProducts?.length > 0 ||
          selectedDeliveryPltf?.length > 0 ||
          (selectedProducts?.length === 0 && selectedDeliveryPltf?.length === 0)
        ) {
          if (
            selectedProducts.length === 0 &&
            selectedDeliveryPltf.length === 0
          )
            return;
          const newRows = [];
          const uniqueProductComponentIds = new Set();

          selectedProducts.forEach((selectedProductId) => {
            for (let i = 0; i < availableMappings.length; i++) {
              const mapping = availableMappings[i];
              const isSelectedProduct = mapping.productId === selectedProductId;
              const isSelectedContract = userContractNumber?.includes(
                mapping.contractNumber,
              );

              // Delivery platform selection is currently disabled for this tab.
              // For AlphaMail products, add rows by selected product + contract.
              const matchesDisabledPlatformFlow =
                !selectedDeliveryPltfId.length &&
                ProductDeliveryPlatforms.includes(mapping.distributor);

              let matchesEnabledPlatformFlow = false;
              if (selectedDeliveryPltf.length > 0) {
                matchesEnabledPlatformFlow = selectedDeliveryPltfId.some(
                  (id) => id == mapping.distributorId,
                );
              }

              if (
                isSelectedProduct &&
                isSelectedContract &&
                (matchesDisabledPlatformFlow || matchesEnabledPlatformFlow)
              ) {
                const pcId = mapping.productComponentId;
                if (!uniqueProductComponentIds.has(pcId)) {
                  newRows.push(mapping);
                  uniqueProductComponentIds.add(pcId);
                }
              }
            }
          });

          let filteredRows = newRows.filter(
            (row) => !initialEntitlementIds.includes(row.productComponentId),
          );
          filteredRows.push(
            ...initialEntitlements.filter((row) =>
              newRows.some(
                (ie) =>
                  ie.productComponentId === row.productComponentId &&
                  (row.status === Cancelled ||
                    row.status === PendingInactive ||
                    row.status === Rejected),
              ),
            ),
          );

          let currentlySelectedEntitlements =
            productEntitlementgridApi?.current?.api?.getSelectedRows();

          const selectedRows = (currentlySelectedEntitlements || []).filter(
            (se) =>
              !(
                [Cancelled, PendingInactive, Rejected].includes(se.status) &&
                (filteredRows?.length == 0 ||
                  filteredRows?.some(
                    (fr) => fr.productComponentId !== se.productComponentId,
                  ))
              ),
          );
          const deselectedRows = productRowData?.filter(
            (row) =>
              row.status &&
              !selectedRows?.some(
                (selectedRow) =>
                  selectedRow.productComponentId === row.productComponentId,
              ) &&
              !filteredRows?.some(
                (fr) => fr.productComponentId === row.productComponentId,
              ),
          );
          setDeselectRowsData(deselectedRows);
          //add unique rows because cancelled and pending inactive already added filtered rows variable
          setProductRowData([
            ...filteredRows,
            ...(initialEntitlements?.filter(
              (x) =>
                !filteredRows.some(
                  (y) => x.productComponentId === y.productComponentId,
                ),
            ) || []),
          ]);
          productEntitlementgridApi.current.api.hideOverlay();

          setTimeout(() => {
            const postData = {
              userEntitlements: getPostEntitlementChanges(),
              userId: UserId,
            };
            dispatch({
              type: ReduxAction.updateEntitlement,
              payload: {
                updatedEntitlements: postData,
                initialEntitlements: initialEntitlements,
              },
            });
          }, 300);
        }
      }
      return () => {
        isMounted = false;
      };
    }, [selectedProducts, selectedDeliveryPltf]);

    useEffect(() => {
      let isMounted = true;
      const reduceCallback = setTimeout(() => {
        if (productRowData && isMounted) {
          if (productRowData.length > 0) {
            setSelectionsInGrid();
          } else {
            if (
              productRowData.length === 0 &&
              initialEntitlements?.length === 0
            ) {
              setIsEntitlementsChanged(false);
            }
          }
        }
      }, 100);
      return () => {
        clearTimeout(reduceCallback);
        isMounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productRowData]);
    //useeffect ends

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target) &&
          !event.target.classList.contains('info-icon')
        ) {
          setTooltip({});
        }
      };

      if (tooltip.showTooltip) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [tooltip.showTooltip]);

    useEffect(() => {
      if (tooltip.showTooltip && gridContainerRef.current) {
        const scrollContainer =
          gridContainerRef.current.querySelector('.ag-body-viewport');
        const handleScroll = () => {
          const rowElement = document.querySelector(
            `[data-row-id="${tooltip.rowId}"]`,
          );
          if (rowElement) {
            const gridRect = scrollContainer.getBoundingClientRect();
            const rowRect = rowElement.getBoundingClientRect();

            // hide tooltip when the row is fully outside the viewport
            if (
              rowRect.bottom <= gridRect.top ||
              rowRect.top >= gridRect.bottom
            ) {
              setTooltip({});
            }
          } else {
            setTooltip({});
          }
        };

        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', handleScroll);
        }

        return () => {
          if (scrollContainer) {
            scrollContainer.removeEventListener('scroll', handleScroll);
          }
        };
      }
    }, [tooltip.showTooltip, tooltip.rowId]);

    const getColumnClass = (params) => {
      if (
        (params?.node?.data?.status === Cancelled ||
          params?.node?.data?.status === PendingInactive ||
          params?.node?.data?.status === Rejected) &&
        !isRowDataLoaded
      ) {
        return 'unselected-entitlement-row';
      }
      if (
        (!params?.node?.data?.status ||
          params?.node?.data?.status === Cancelled ||
          params?.node?.data?.status === PendingInactive ||
          params?.node?.data?.status === Rejected) &&
        params?.node?.isSelected() &&
        isRowDataLoaded
      ) {
        return 'new-selected-entitlement-row';
      }
      if (
        params?.node?.data?.status &&
        !params?.node?.isSelected() &&
        isRowDataLoaded
      ) {
        return 'unselected-entitlement-row';
      } else {
        return '';
      }
    };

    const distributorGetter = (params) => {
      if (params?.data)
        return (
          params?.data?.distributorDisplayName +
          ' : ' +
          params?.data?.distributorPlatformDisplayName +
          (params?.data?.deliveryType?.toLowerCase() !== 'web'
            ? ' (' + params?.data?.deliveryType + ')'
            : '')
        );

      return '';
    };

    const handleTooltipClick = (event, rowId, blockedReason) => {
      event.stopPropagation();

      if (tooltip.rowId === rowId) {
        setTooltip({});
      } else {
        setTooltip({
          showTooltip: true,
          rowId,
          text: blockedReason,
        });
      }
    };

    // Custom Checkbox Cell Renderer
    const CheckboxCellRenderer = (props) => {
      const isBlocked =
        props.data?.status === Blocked || props.data?.status === 'Blocked';
      if (editData?.groupId || isViewerUser) {
        return <span>{props.data?.packageName}</span>;
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={props.node.isSelected()}
            disabled={isBlocked}
            onChange={() => {
              if (!isBlocked) {
                props.node.setSelected(!props.node.isSelected());
              }
            }}
            style={{
              cursor: isBlocked ? 'not-allowed' : 'pointer',
              height: '17px',
              width: '17px',
              minWidth: '17px',
              minHeight: '17px',
              accentColor: 'var(--ag-alpine-active-color)',
            }}
            title={
              isBlocked
                ? 'Entitlement is currently blocked. There are no actions pending at your end. Hover over the "Blocked reason" for more details.'
                : ''
            }
          />
          <span>{props.data?.packageName}</span>
        </div>
      );
    };

    const productEntitlementsColumns = [
      {
        headerName: '',
        field: 'id',
        resizable: true,
        filter: 'agTextColumnFilter',
        suppressHeaderMenuButton: true,
        // suppressCellSelection: true,
        sortable: false,
        hide: true,
      },
      {
        headerName: 'Package',
        headerTooltip: 'Package',
        field: 'packageName',
        tooltipField: 'packageName',
        initialFlex: 3,
        cellClass: getColumnClass,
        headerCheckboxSelection:
          editData?.groupId || isViewerUser ? false : true,
        cellRenderer: CheckboxCellRenderer,
      },
      {
        headerName: 'Product',
        headerTooltip: 'Product',
        field: 'productName',
        tooltipField: 'productName',
        initialFlex: 2,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Delivery Platform (Type)',
        headerTooltip: 'Delivery Platform (Type)',
        field: 'deliveryPlatform',
        initialFlex: 3,
        valueGetter: distributorGetter,
        tooltipValueGetter: distributorGetter,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Contract ID',
        headerTooltip: 'Contract ID',
        field: 'contractNumber',
        tooltipField: 'contractNumber',
        initialFlex: 2,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Status',
        headerTooltip: 'Status',
        field: 'status',
        tooltipValueGetter: (params) => getStatusDefinition(params.value),
        headerComponent: StatusDefinitionHeader,
        initialFlex: 2,
        cellClass: getColumnClass,
        cellRenderer: (params) => {
          const isTooltipVisible =
            tooltip.showTooltip && tooltip.rowId === params.data.id;

          return (
            <div style={{ position: 'relative' }} data-row-id={params.data.id}>
              <span>
                {params.value}
                {params.value === 'Blocked' && (
                  <img
                    src={InfoIcon}
                    className="info-icon"
                    style={{
                      cursor: 'pointer',
                      marginLeft: '5px',
                      width: '20px',
                    }}
                    title={params.data.blockedReason}
                    onClick={(event) => {
                      handleTooltipClick(
                        event,
                        params.data.id,
                        params.data.blockedReason,
                      );
                    }}
                  />
                )}
                {isTooltipVisible && (
                  <StickyTooltip
                    ref={tooltipRef}
                    text={tooltip.text}
                    onClose={() => setTooltip({})}
                  />
                )}
              </span>
            </div>
          );
        },
      },
    ];

    const productCompare = (current, next) => {
      if (current.productName < next.productName) {
        return -1;
      }
      if (current.productName > next.productName) {
        return 1;
      }
      return 0;
    };

    const getContractProducts = (signal) => {
      if (contractNumber) {
        let productDistributors = ProductDeliveryPlatforms?.map(
          (t) => '"' + t + '"',
        ).join(',');
        let productTypes = ProductTypes?.map((t) => '"' + t + '"').join(',');

        let contractFilter = '';
        const contractsForFilter =
          userContractNumber && userContractNumber.length > 0
            ? userContractNumber
            : contractNumber
              ? [contractNumber]
              : [];
        if (contractsForFilter.length > 0) {
          contractFilter = `contractNumber in (${contractsForFilter
            .map((num) => `"${num}"`)
            .join(',')})`;
        }

        let queryParams = {
          pageSize: 50000,
          filter: `${contractFilter} ${
            productDistributors
              ? `AND (distributor in (${productDistributors}) OR producttype in (${productTypes}))`
              : ''
          }`,
          Field:
            'productId,productName,productType,packageName,productComponentId,distributorId,distributor,distributorPlatform,distributorDisplayName,distributorPlatformDisplayName,deliveryType,contractNumber,platformCategories',
        };
        smartHttp
          .get(Endpoints.contractProductsApi, queryParams, {
            axiosConfig: { signal },
            requestId: `get-contract-products-${contractNumber}`,
          })
          .then((res) => {
            if (res) {
              const allProducts =
                res?.data?.results?.map((item) => ({
                  ...item,
                  indicatePlatform: distributorToIndicate.some(
                    (el) => el === item.distributorId,
                  ),
                })) || [];
              const filteredMappings = Array.from(
                new Map(
                  allProducts.map((item) => [item.productComponentId, item]),
                ).values(),
              );
              setAvailableMappings(filteredMappings || []);
              const uniquePrdDist = Array.from(
                new Map(
                  allProducts.map((item) => [
                    `${item.productId}_${item.distributorId}`,
                    item,
                  ]),
                ).values(),
              );
              setUniqAvailableMappings(uniquePrdDist || []);

              // Unique products based on productId and productComponentId
              const uniqueProducts = Array.from(
                new Map(
                  allProducts.map((item) => [`${item.productId}`, item]),
                ).values(),
              );
              uniqueProducts?.sort(productCompare);
              setProductDataset({ results: uniqueProducts || [] });

              // FEATURE DISABLED (Release july-2026) - Delivery Platform Dropdown
              // const uniqueDeliveryPlatforms = Array.from(
              //   new Map(allProducts.map((item) => [`${item.distributorId}`, item])).values(),
              // );
              // setDeliveryPltfDataset({ results: uniqueDeliveryPlatforms || [] });
            }
          })
          .catch((err) => {
            if (err.code !== 'ERR_CANCELED') setProductDataset([]);
          });
      }
    };

    const callBackProducts = (params) => {
      let dsts = [];
      params?.forEach((dst) => {
        dsts.push(dst?.productId);
      });
      setSelectedProducts(dsts);
    };

    // FEATURE DISABLED (Release july-2026) - Delivery Platform Dropdown
    // const callBackDistributors = (params) => { ... };

    // FEATURE DISABLED (Release july-2026) - Cascading dropdown logic
    // const cascadingProductDeliveryPlatform = () => { ... };
    // const cascadingProductDeliveryPlatformMemo = useMemo(cascadingProductDeliveryPlatform, [...]);

    const onSelectionChanged = debounce(() => {
      productEntitlementgridApi.current?.api.refreshCells({
        force: true,
      });

      // force blocked rows to remain selected
      productEntitlementgridApi.current?.api.forEachNode((node) => {
        if (node.data?.status === Blocked) {
          node.setSelected(true);
        }
      });

      validateEntitlementChange();

      // capture currently selected product rows for ManageUserIds
      const selectedNow =
        productEntitlementgridApi.current?.api?.getSelectedRows() || [];

      // keep existing product-updates dispatch
      setTimeout(() => {
        dispatch({
          type: ReduxAction.updateProductEntitlement,
          payload: {
            updatedProductEntitlements: getEntitlementChanges(),
          },
        });
      }, 300);

      dispatch({
        type: ReduxAction.updateEntitlement,
        payload: {
          updatedEntitlements: {
            userEntitlements: getEntitlementChanges(),
            userId: UserId,
          },
          // show these distributors immediately in MUI
          selectedEntitlements: selectedNow,
        },
      });
    }, 100);

    const validateData = (entitlementPostData, intialEntitlementData) => {
      let ManageUserIdDistributors =
        store.getState()?.dbConfig.ManageUserIdDistributors;
      let postDetailEntitlements =
        entitlementPostData?.userEntitlements?.length > 0
          ? entitlementPostData?.userEntitlements
          : [];
      let managedUsers = distributorDataSet?.results?.filter(
        (x) =>
          RequiredPlatformCategory.includes(x.platformCategories) &&
          ManageUserIdDistributors.some(
            (item) => item.distributorId === x.distributorId,
          ),
      );
      let requiredEntitlements = postDetailEntitlements.filter(
        (en) =>
          managedUsers?.some((md) => md.distributorId === en.distributorId) &&
          (en.status === Active ||
            en.status === PendingActive ||
            en.status === Draft),
      );
      return requiredEntitlements;
    };

    const updateProductEntitlements = (postData) => {
      setLoading(true);

      smartHttp
        .post(Endpoints.userentitlementsApi, postData)
        .then(() => {
          smartHttp.clearCache(Endpoints.userentitlementsApi);
          // reset dropdown selections after successful save
          setSelectedProducts([]);
          // FEATURE DISABLED (Release july-2026)
          // setSelectedDeliveryPltf([]);
          // setSelectedDeliveryPltfId([]);
          setSelectedParentCascade(undefined);
          setFilteredProducts(productDataset);
          // FEATURE DISABLED (Release july-2026)
          // setFilteredDeliveryPlatforms(deliveryPltfDataset);
          setResetNonce((n) => n + 1);

          getUsers();
          onAddEditModalClose();
          Notify({
            alert: true,
            type: 'success',
            title: 'User Entitlements have been updated',
          });
        })
        .catch((err) => {
          // FEATURE DISABLED (Release july-2026) - LME Governance Check
          // let errorMsg =
          //   err?.response?.data === -3
          //     ? Verbiage.lmeEntitlementsGovernanceFailed
          //     : GenericError.somethingWentWrong;
          let errorMsg = GenericError.somethingWentWrong;
          Notify({
            alert: true,
            type: 'error',
            title: errorMsg,
          });
        })
        .finally(() => setLoading(false));
    };

    const updateEntitlements = () => {
      try {
        let entitlementChanges = getEntitlementChanges();
        let postData = {
          userEntitlements: entitlementChanges,
          userId: UserId,
        };
        let currentlySelected = [];
        productEntitlementgridApi?.current?.api
          .getSelectedNodes()
          .forEach((node) => {
            currentlySelected.push(node.data);
          });
        dispatch({
          type: ReduxAction.updateProductEntitlement,
          payload: {
            updatedProductEntitlements: postData,
            selectedProductEntitlements: currentlySelected,
          },
        });

        // check for channel partner match in changed entitlements
        let partnerMatch = false;

        let ManageUserIdDistributors =
          store.getState()?.dbConfig.ManageUserIdDistributors;

        for (const newEntitlement of entitlementChanges) {
          let match = distributorDataSet?.results?.find(
            (matchingEntitlement) =>
              matchingEntitlement.platformCategories == 'Channel Partners' &&
              matchingEntitlement.distributorId ==
                newEntitlement.distributorId &&
              ManageUserIdDistributors.some(
                (item) =>
                  item.distributorId === matchingEntitlement.distributorId,
              ),
          );

          if (
            match &&
            [PendingActive, Active, Draft].some(
              (status) => newEntitlement?.status === status,
            )
          ) {
            partnerMatch = true;
            break;
          }
        }

        if (partnerMatch) {
          // dispatch({
          //   type: ReduxAction.updateProductEntitlement,
          //   payload: {
          //     updatedProductEntitlements: entitlementChanges,
          //   },
          // });
          // const selectedNow =
          //   productEntitlementgridApi.current?.api?.getSelectedRows() || [];
          // dispatch({
          //   type: ReduxAction.updateEntitlement,
          //   payload: { selectedEntitlements: selectedNow },
          // });
          setIsUserIdEnabled(true);
          setActiveTab('manage-user-ids-tab');
        } else {
          // Handle existing logic for backward compatibility
          let entitlementDetails = store.getState()?.entitlementDetails;
          let postPackageEntData = entitlementDetails?.entitlementsToUpdate;
          let initialEntitlementData = entitlementDetails?.initialEntitlements;

          if (postPackageEntData || initialEntitlementData) {
            let requiredEntitlements = validateData(
              postPackageEntData,
              initialEntitlementData,
            );
            if (requiredEntitlements && requiredEntitlements.length > 0) {
              dispatch({
                type: ReduxAction.updateProductEntitlement,
                payload: {
                  updatedProductEntitlements: entitlementChanges,
                },
              });
              const selectedNow =
                productEntitlementgridApi.current?.api?.getSelectedRows() || [];
              dispatch({
                type: ReduxAction.updateEntitlement,
                payload: { selectedEntitlements: selectedNow },
              });
              setIsUserIdEnabled(true);
              setActiveTab('manage-user-ids-tab');
            } else {
              postPackageEntData?.userEntitlements.forEach((entData) => {
                // filter out duplicates
                const existingIndex = entitlementChanges.findIndex(
                  (item) =>
                    item.productcomponentid === entData.productcomponentid,
                );

                if (existingIndex === -1) {
                  entitlementChanges.push(entData);
                }
              });
              postData = {
                userEntitlements: entitlementChanges,
                userId: UserId,
              };
              updateProductEntitlements(postData);
            }
          } else {
            // save product entitlements
            updateProductEntitlements(postData);
          }
        }
      } catch (err) {
        setLoading(false);
      }
    };

    const getPostEntitlementChanges = () => {
      let contactId = editData.contactId !== '' ? editData.contactId : -1;
      let entitlementChanges = [];

      productEntitlementgridApi.current.api.forEachNode((node) => {
        // remove entitlements that have a status and aren't selected
        if (
          node.data?.status &&
          [Active, PendingActive, PendingApproval, Blocked, Draft].some(
            (status) => node.data?.status === status,
          ) &&
          !node.isSelected()
        ) {
          entitlementChanges.push({
            contactid: contactId,
            productcomponentid: node.data.productComponentId,
            status: PendingInactive,
            contractNumber: node.data.contractNumber,
            distributor: node.data.distributor,
            distributorDisplayName: node.data.distributorDisplayName,
            distributorPlatform: node.data.distributorPlatform,
            distributorPlatformDisplayName:
              node.data.distributorPlatformDisplayName,
            distributorId: node.data.distributorId,
          });
        }
        // add entitlements that don't have a status and are selected
        if (
          (!node.data?.status ||
            [Cancelled, PendingInactive, Rejected].some(
              (status) => node.data?.status === status,
            )) &&
          node.isSelected()
        ) {
          entitlementChanges.push({
            contactid: contactId,
            productcomponentid: node.data.productComponentId,
            // FEATURE DISABLED (Release july-2026) - status: isBPMReferral ? Draft : PendingActive,
            status: PendingActive,
            contractNumber: node.data.contractNumber,
            distributor: node.data.distributor,
            distributorDisplayName: node.data.distributorDisplayName,
            distributorPlatform: node.data.distributorPlatform,
            distributorPlatformDisplayName:
              node.data.distributorPlatformDisplayName,
            distributorId: node.data.distributorId,
          });
        }
      });
      return entitlementChanges;
    };

    return (
      <>
        <Container className={editData?.groupId ? 'pt-3' : 'pt-2-5'}>
          <Row>
            <Col className="mb-1">
              {!editData?.groupId && (
                <span className="email-preference-note-label">
                  Please select CI Market Reports & Insights the user shall
                  receive via Email
                </span>
              )}
              {editData?.groupId && (
                <span className="email-preference-note-label">
                  This user belongs to a group and cannot be edited
                </span>
              )}
            </Col>
          </Row>
          {!editData?.groupId && (
            <Row>
              <Col md={6}>
                <div>
                  <ProductsDropdown
                    key={`products-${resetNonce}`}
                    DataSet={filteredProducts}
                    callBackProducts={callBackProducts}
                    disabled={editData?.groupId}
                  />
                </div>
              </Col>
              {/* FEATURE DISABLED (Release july-2026) - Delivery Platform Dropdown */}
              {/* <Col md={6}>
                <div>
                  <ProductDistributorsDropdown
                    key={`dists-${resetNonce}`}
                    DataSet={filteredDeliveryPlatforms}
                    callBackDistributors={callBackDistributors}
                    disabled={editData?.groupId}
                  />
                </div>
              </Col> */}
            </Row>
          )}
          <Row className="grid-tab-row">
            <div
              className="ag-grid-modal-content ag-grid-modal-content-full-height"
              ref={gridContainerRef}
            >
              <AgGridComponent
                config={defaultConfig}
                defaultColumnDef={defaultColdef}
                data={productRowData}
                columns={productEntitlementsColumns}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onProductEntitlementGridReadyHandler}
              />
            </div>
          </Row>
          <Row
            className={
              editData?.groupId ? 'modal-row' : 'modal-row entitlement-fix'
            }
          >
            <div className="col-7">
              {!editData?.groupId && (
                <div>
                  <span title="Currently subscribed by other users">
                    🟢 Currently subscribed by other users
                  </span>
                </div>
              )}
              <span className="email-preference-note-label">
                <span className="notes-asterisk-label">*</span>
                Please note that this user can self-manage and customise Email
                alerts on Platts Connect
              </span>
            </div>
            <Form className="col-5">
              <Form.Group className="user-save-btn-body">
                <button
                  type="button"
                  disabled={
                    !isEntitlementsChanged || editData?.groupId || isViewerUser
                  }
                  className="user-save-btn btn btn-dark float-end btn-opacity"
                  onClick={updateEntitlements}
                >
                  {editMode ? 'Update' : 'Save'}
                </button>
              </Form.Group>
            </Form>
          </Row>
        </Container>
      </>
    );
  },
  (op, np) =>
    op?.editData === np?.editData &&
    op?.distributorToIndicate === np?.distributorToIndicate,
);

export default UserProductEntitlements;
